import React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  useTheme,
  Autocomplete,
} from "@mui/material";
import { FaTimes } from "react-icons/fa";
import dayjs, { Dayjs } from "dayjs";
import ITopupDateRangePicker from "../ITopupDateRangePicker";
import { useEffect, useState, useCallback } from "react";
import { useLazyGetCustomersQuery } from "../../Services/CustomerService";
import DateRangeFilter from "../DateRangeFilter";
import ProductDenominationFilter from "../ProductDenominationFilter";

export interface PaymentFilters {
  startDate?: string;
  endDate?: string;
  paymentStatus: string;
  orderNumber: string;
  customerName?: string;
  /** Chosen from the dropdown, not typed. */
  productId?: string;
  /** The SKU of a denomination of that product. Depends on productId. */
  productDenominationsku?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filters: PaymentFilters) => void;
  onClearFilters: () => void;
  filters: PaymentFilters;
  onFilterChange: (name: keyof PaymentFilters, value: any) => void;

}

const PaymentReportsSlidingFilter: React.FC<Props> = ({
  open,
  onClose,
  onApplyFilters,
  onClearFilters,
  filters,
  onFilterChange,
}) => {
  const theme = useTheme();
  const [
    getCustomersTrigger,
    { data: customerData, isLoading: customerLoading }
  ] = useLazyGetCustomersQuery();

  const [currentPage, setCurrentPage] = useState(1);
  const [allCustomers, setAllCustomers] = useState<any[]>([]);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDateRangeValid, setIsDateRangeValid] = useState(true);

  useEffect(() => {
    setCurrentPage(1);
    setAllCustomers([]);
    setHasMoreData(true);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm.trim()) {
        setCurrentPage(1);
        setAllCustomers([]);
        setHasMoreData(true);

        getCustomersTrigger({
          filters: [],
          page: 1,
          pageSize: 50,
          sortBy: "createdAt",
          sortOrder: "desc",
          search: searchTerm.trim(),
        });
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm, getCustomersTrigger]);

  useEffect(() => {
    if (customerData?.data) {
      if (currentPage === 1) {
        setAllCustomers(customerData.data);
      } else {
        setAllCustomers((prev) => [...prev, ...customerData.data]);
      }
      const totalPages = Math.ceil(customerData.totalRecords / 20);
      setHasMoreData(currentPage < totalPages);
      setIsLoadingMore(false);
    }
  }, [customerData, currentPage]);

  const loadMoreData = useCallback(() => {
    if (!isLoadingMore && hasMoreData && !customerLoading) {
      setIsLoadingMore(true);
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);

      getCustomersTrigger({
        filters: [],
        page: nextPage,
        pageSize: 50,
        sortBy: "createdAt",
        sortOrder: "desc",
        search: "",
      });
    }
  }, [currentPage, hasMoreData, isLoadingMore, customerLoading, getCustomersTrigger]);

  const loadAllRecords = useCallback(() => {
    if (!customerLoading && !isLoadingMore) {
      setCurrentPage(1);
      setAllCustomers([]);
      setHasMoreData(true);

      getCustomersTrigger({
        page: 1,
        pageSize: 50,
        filters: [],
        search: "",
        sortBy: "createdAt",
        sortOrder: "desc",
      });
    }
  }, [customerLoading, isLoadingMore, getCustomersTrigger]);

  useEffect(() => {
    setSearchTerm(filters.customerName || "");
  }, [filters.customerName]);

  const searchOptions = React.useMemo(() => {
    if (!allCustomers.length) return [] as string[];
    const options: string[] = [];
    allCustomers.forEach((customer: any) => {
      if (customer?.firstName && customer?.lastName) {
        const fullName = `${customer.firstName} ${customer?.lastName} - ${customer?.email}`;
        if (!options.includes(fullName)) options.push(fullName);
      }
    });
    return options.sort();
  }, [allCustomers]);

  const getSearchValue = (term: string) => {
    if (!term) return "";
    if (term.includes(" - ")) {
      const parts = term.split(" - ");
      if (parts.length === 2) {
        const email = parts[1];
        return email;
      }
    }
    for (const customer of allCustomers) {
      const nameAndEmail = `${customer?.firstName || ""} ${customer?.lastName || ""} - ${customer?.email || ""}`.trim();
      if (nameAndEmail === term && customer?.email) {
        return customer?.email;
      }
      if (customer?.email === term) return term;
      if (customer?.phoneNumber === term) return term;
    }
    return term;
  };

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleClear = () => {
    onClearFilters();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 380,
          padding: 0,
        },
      }}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
        "& .MuiDrawer-paper": {
          overflow: "visible",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: "12px 16px",
            backgroundColor: theme.palette.primary.main,
            color: "white",
          }}
        >
          <Typography variant="h6" component="h2">Apply Filters</Typography>
          <IconButton onClick={onClose} sx={{ color: "white" }} size="small">
            <FaTimes />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Order Number */}
          <TextField
            label="Order / Payment / Reference #"
            placeholder="Order #, Payment ID or Bank Reference #"
            variant="outlined"
            size="small"
            value={filters.orderNumber}
            onChange={(e) => onFilterChange("orderNumber", e.target.value)}
            type="search"
          />

          {/* Customer Name  */}

          <Box>
            <Autocomplete
              freeSolo
              options={searchOptions}
              value={filters.customerName || ''}
              onInputChange={(event, newInputValue) => {
                const searchValue = getSearchValue(newInputValue || '');
                setSearchTerm(searchValue);
                onFilterChange('customerName', searchValue);
              }}
              onChange={(event, newValue) => {
                const searchValue = getSearchValue(newValue || '');
                setSearchTerm(searchValue);
                onFilterChange('customerName', searchValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Search Customer"
                  placeholder="Search by Customer name"
                  onFocus={loadAllRecords}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.paper',
                    },
                  }}
                />
              )}
              ListboxProps={{
                onScroll: (event: React.SyntheticEvent) => {
                  const listboxNode = event.currentTarget;
                  if (
                    listboxNode.scrollTop + listboxNode.clientHeight >=
                    listboxNode.scrollHeight - 10
                  ) {
                    loadMoreData();
                  }
                },
                style: {
                  maxHeight: '200px',
                },
              }}
              sx={{
                '& .MuiAutocomplete-popper': {
                  zIndex: 9999,
                },
              }}
              loading={customerLoading || isLoadingMore}
              loadingText={isLoadingMore ? "Loading more..." : "Loading suggestions..."}
              noOptionsText={hasMoreData ? "Keep typing to search..." : "No matching results"}
            />
          </Box>
          {/* Payment Status */}
          <FormControl fullWidth size="small">
            <InputLabel>Payment Status</InputLabel>
            <Select
              value={filters.paymentStatus}
              label="Payment Status"
              onChange={(e) => onFilterChange("paymentStatus", e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    zIndex: 9999,
                  },
                },
                disablePortal: true,
              }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="unpaid">Unpaid</MenuItem>
              {/* <MenuItem value="failed">Failed</MenuItem> */}
            </Select>
          </FormControl>

          {/*
            Product and Denomination.

            A payment carries neither -- it belongs to an order, and the
            products are on that order's items -- so the server resolves
            these to the orders holding them. Offered here all the same,
            because "which payments were for this product" is the question
            this screen is opened with.
          */}
          <ProductDenominationFilter
            productId={filters.productId}
            denominationSku={filters.productDenominationsku}
            onChange={(field, value) => onFilterChange(field as any, value)}
          />

          <DateRangeFilter
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={(key, value) => onFilterChange(key as keyof PaymentFilters, value)}
            label="Date Range"
            onValidityChange={setIsDateRangeValid}
          />

        </Box>

        <Divider />

        {/* Footer */}
        <Box sx={{ p: 2, display: "flex", gap: 2 }}>
          <Button variant="outlined" onClick={handleClear} sx={{ flex: 1 }}>
            Clear All
          </Button>
          <Button variant="contained" onClick={handleApply} sx={{ flex: 1 }} disabled={!isDateRangeValid}>
            Apply Filters
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default PaymentReportsSlidingFilter;
